// @flow
import ResourceCollection from "../base/ResourceCollection";
import ChoiceAttributeOptionModel from "./ChoiceAttributeOptionModel";
import ListHeaderModel from "../list/ListHeaderModel";

import { getBasePathModularUI } from "../../constants";

import type { ChoiceAttributeOptionModelOptions } from "./ChoiceAttributeOptionModel";
/**
 */
class ChoiceAttributeOptionCollection extends ResourceCollection<ChoiceAttributeOptionModel> {
  _modelOptions: ChoiceAttributeOptionModelOptions;
  _headers: Array<ListHeaderModel> = [];
  _sortOptions: boolean = false;

  constructor(modelOptions?: ChoiceAttributeOptionModelOptions) {
    super();
    this._modelOptions = modelOptions || {
      origin: "",
      contextPath: getBasePathModularUI(),
      referenceDate: new Date().toISOString().split("T")[0],
    };
  }

  /**
   */
  set sortOptions(sortOptions: boolean) {
    this._sortOptions = sortOptions;

    this.collection.forEach((option) => {
      if (option.children) {
        option.children.sortOptions = sortOptions;
      }
    });
  }

  /**
   */
  get sortOptions(): boolean {
    return this._sortOptions;
  }

  /**
   */
  static create(
    data: Object = {},
    contributions: Object = {},
    modelOptions?: ChoiceAttributeOptionModelOptions,
  ): ChoiceAttributeOptionCollection {
    const collection = new ChoiceAttributeOptionCollection(modelOptions);

    if (contributions.type === "boolean") {
      collection.addBooleanOptions(data.value, contributions);
    } else if (data.dynamicschema != null) {
      collection.addDynamicSchema(data, contributions);
    } else if (contributions.options != null) {
      collection.addOptions(data, contributions);
    }

    if (contributions.children != null) {
      collection.addHeaders(contributions.children);
    }

    return collection;
  }

  /**
   * Retrieve all items in collection
   */
  get all(): Array<ChoiceAttributeOptionModel> {
    return this.sortOptions
      ? this.collection.sort((a, b) => a.label.localeCompare(b.label))
      : this.collection;
  }

  /**
   */
  getSelectedOptions(data: Object): Array<string> {
    const selectedOptions = [];

    if (Array.isArray(data.options)) {
      selectedOptions.push(
        ...data.options
          .filter((option) => option.selected)
          .map((option) => option.key),
      );
    } else if (Array.isArray(data.value)) {
      selectedOptions.push(...data.value);
    } else if (typeof data.value === "boolean") {
      selectedOptions.push(data.value.toString());
    } else {
      selectedOptions.push(data.value);
    }

    return selectedOptions;
  }

  /**
   */
  getOptionCount(code: string, options: Array<Object>): number | null {
    if (Array.isArray(options)) {
      const foundOption = options.find((option) => option.key === code);
      if (foundOption) {
        return foundOption.count;
      }
    }

    return null;
  }

  /**
   */
  addOptions(
    data: Object,
    contributions: Object,
  ): ChoiceAttributeOptionCollection {
    const selectedValues = this.getSelectedOptions(data);

    contributions.options.forEach((optionContributions) => {
      const optionCode = optionContributions.code || optionContributions.key;

      this.addOption(selectedValues, {
        ...optionContributions,
        count: this.getOptionCount(optionCode, data.options),
        optionType: contributions.optionType ?? "generic",
      });
    });

    return this;
  }

  /**
   * Create options based on data and dynamischema
   */
  addDynamicSchema(
    data: Object,
    contributions: Object,
  ): ChoiceAttributeOptionCollection {
    const selectedValues = this.getSelectedOptions(data);
    data.dynamicschema.forEach((optionDynamicschema) => {
      this.addOption(selectedValues, {
        ...optionDynamicschema,
        count: this.getOptionCount(optionDynamicschema.code, data.options),
        optionType: contributions.optionType ?? "generic",
        elementsContributions: contributions.children,
      });
    });

    return this;
  }

  /**
   */
  addBooleanOption(
    code: "true" | "false",
    defaultLabel: string,
    data: string | Array<string>,
    options: Array<Object> = [],
  ) {
    const foundOption = options.find((option) => option.code === code);

    const label = foundOption ? foundOption.label : defaultLabel;

    this.addOption(data, {
      code,
      label,
      isBooleanType: true,
      hasAlternativeLabel: foundOption != null,
    });
  }

  /**
   * Create two static options for true and false,
   * there might be alternative labels configured for each option value
   */
  addBooleanOptions(
    value: any,
    contributions: Object,
  ): ChoiceAttributeOptionCollection {
    const optionValue = value == null ? [] : [value.toString()];

    this.addBooleanOption("true", "Yes", optionValue, contributions.options);
    this.addBooleanOption("false", "No", optionValue, contributions.options);

    return this;
  }

  /**
   */
  addOption(
    data: string | Array<string>,
    contributions: Object,
  ): ChoiceAttributeOptionCollection {
    const optionModel = new ChoiceAttributeOptionModel(
      Array.isArray(data) ? data : [data],
      contributions,
      this._modelOptions,
    );

    this.add(optionModel);

    return this;
  }

  /**
   */
  getOptionByCode(code: string): ChoiceAttributeOptionModel | void {
    return this.collection.find((option) => option.code === code);
  }

  /**
   */
  hasOptionByCode(code: string): boolean {
    return this.collection.some((option) => option.code === code);
  }

  /**
   * Get all options that have selected property true
   */
  get selected(): Array<ChoiceAttributeOptionModel> {
    const selectedOptions = [];

    this.collection.forEach((option: ChoiceAttributeOptionModel) => {
      if (option.selected && option.selected === true) {
        selectedOptions.push(option);
      }

      if (option.children) {
        selectedOptions.push(...option.children.selected);
      }
    });

    return selectedOptions;
  }

  /**
   * Deselect all options
   */
  deselectAll() {
    this.collection = this.collection.map((option) => {
      const newOption = option.clone();

      newOption.selected = false;

      if (newOption.children) {
        newOption.children.deselectAll();
      }

      return newOption;
    });
  }

  /**
   * Select an option by it's code
   */
  select(optionCode: string) {
    this.toggle(optionCode, "select");
  }

  /**
   * Deselect an option by it's code
   */
  deselect(optionCode: string) {
    this.toggle(optionCode, "deselect");
  }

  /**
   * Toggle an option by it's code
   */
  toggle(optionCode: string, action: "select" | "deselect") {
    this.collection = this.collection.map((option) => {
      const newOption = option.clone();

      if (newOption.code === optionCode) {
        newOption.selected = action === "select";
      } else if (newOption.children) {
        newOption.children.toggle(optionCode, action);
      }

      return newOption;
    });
  }

  /**
   */
  setReferenceDate(date: ISO_DATE): Array<ChoiceAttributeOptionModel> {
    return this.collection.map((option) => {
      option.referenceDate = date;
      if (option.children) {
        option.children.referenceDate = date;
      }

      return option;
    });
  }

  /**
   */
  set referenceDate(date: ISO_DATE) {
    this.collection = this.setReferenceDate(date);
  }

  /**
   */
  addHeaders(headers: Array<Object>) {
    this._headers = headers.map((child) => new ListHeaderModel(child));
  }

  /**
   */
  get headers(): Array<ListHeaderModel> {
    if (this._headers) {
      return this._headers;
    }

    return [];
  }

  /**
   */
  mergeOptions(
    withOptions: ChoiceAttributeOptionCollection,
    addNotExistingOptions: boolean = false,
    removeNotExistingOptions: boolean = false,
  ) {
    const newCollection = [];

    this.collection.forEach((option) => {
      const foundOption = withOptions.find(
        (withOption) => withOption.code === option.code,
      );
      if (foundOption) {
        option.mergeOption(foundOption);
        newCollection.push(option);
      } else if (!removeNotExistingOptions) {
        newCollection.push(option);
      }
    });

    if (addNotExistingOptions) {
      this.collection = [
        ...newCollection,
        ...withOptions.filter(
          (option) => !this.collection.some((opt) => opt.code === option.code),
        ),
      ];
    } else {
      this.collection = newCollection;
    }
  }

  /**
   */
  some(
    callbackfn: (
      value: ChoiceAttributeOptionModel,
      index: number,
      array: Array<ChoiceAttributeOptionModel>,
    ) => any,
    thisArg?: any,
  ): boolean {
    return this.collection.some((option, optionIndex, collectionArray) => {
      const result = callbackfn(option, optionIndex, collectionArray);
      if (!result && option.children) {
        return option.children.some(callbackfn, thisArg);
      }
      return result;
    });
  }
}

export default ChoiceAttributeOptionCollection;
